import Bookkeeper from "components/company/Bookkeeper";
import FoundedYearCard from "components/company/cards/FoundedYearCard";
import HeadquartersCard from "components/company/cards/HeadquartersCard";
import IconCard, { CardDescription, CardValue } from "components/company/cards/IconCard";
import IndexParticipationCard from "components/company/cards/IndexParticipationCard";
import LiquidityScoreCard from "components/company/cards/LiquidityScoreCard";
import MarketCapCard from "components/company/cards/MarketCapCard";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import ReclameAquiIcon from "components/icons/ReclameAquiIcon";
import classNames from "components/ui/classNames";
import { tailwindColors } from "components/ui/colors/colorsBundle";
import { parseISO } from "date-fns";
import { ScrollIcon, TableRowsSplitIcon } from "lucide-react";
import { FormattedDate, FormattedMessage } from "react-intl";
import { IBrazilianCompanyIssuerListingSegment } from "types/graphqlTypes";

interface IBrazilianCardsContentProps {
  tickerSymbol: string;
}
export default function BrazilianCardsContent({ tickerSymbol }: IBrazilianCardsContentProps) {
  const issuer = useBrazilianIssuer();
  const listingSegment = getIssuerListingSegment(issuer.listingSegment);

  return (
    <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {/* Market Cap */}
      <MarketCapCard issuer={issuer} />

      {/* Segmento de Listagem */}
      {listingSegment && (
        <IconCard
          primaryColor={tailwindColors.amber[400]}
          secondaryColor={tailwindColors.amber[100]}
          icon={<TableRowsSplitIcon size={14} strokeWidth={1.5} absoluteStrokeWidth={true} />}
        >
          <CardValue className={listingSegment.className}>{listingSegment.label}</CardValue>
          <CardDescription>
            <FormattedMessage defaultMessage="Segmento de Listagem" id="djf3kt" />
          </CardDescription>
        </IconCard>
      )}

      {/* Ano de Fundação */}
      <FoundedYearCard issuer={issuer} />

      {/* Sede */}
      <HeadquartersCard issuer={issuer} />

      {/* Escriturador */}
      {issuer.bookkeeper && (
        <IconCard
          primaryColor={tailwindColors.indigo[400]}
          secondaryColor={tailwindColors.indigo[100]}
          icon={<ScrollIcon size={14} strokeWidth={1.5} absoluteStrokeWidth={true} />}
        >
          <CardValue title={issuer.bookkeeper}>
            <Bookkeeper bookkeeper={issuer.bookkeeper} side="top" />
          </CardValue>
          <CardDescription>
            <FormattedMessage defaultMessage="Escriturador" id="FtruOv" />
          </CardDescription>
        </IconCard>
      )}

      {/* Auditor */}
      {issuer.liquidityScore && <LiquidityScoreCard tickerSymbol={tickerSymbol} issuer={issuer} />}

      {/* Ibovespa */}
      <IndexParticipationCard issuer={issuer} />

      {/* Reclame Aqui */}
      {issuer.reclameAquiRatings && (
        <IconCard
          primaryColor={tailwindColors.green[600]}
          secondaryColor={tailwindColors.green[50]}
          icon={<ReclameAquiIcon className="size-4" />}
        >
          <CardValue>
            <a
              className="inline-flex items-baseline gap-x-1 text-sm font-medium text-gray-500"
              href={issuer.reclameAquiUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              <span className="text-2xl font-bold text-gray-700 hover:underline dark:text-slate-200">
                {issuer.reclameAquiRatings.finalScore}
              </span>
              <span
                className={classNames(
                  "text-sm font-bold italic",
                  getReclameAquiClassificationTextColor(issuer.reclameAquiRatings.finalScore),
                )}
              >
                {issuer.reclameAquiRatings.classification}
              </span>
            </a>
          </CardValue>
          <CardDescription>
            <FormattedMessage defaultMessage="Reclame Aqui" id="QvWc3V" />
            {" • "}
            <FormattedDate value={parseISO(issuer.reclameAquiRatings.asOfDate)} />
          </CardDescription>
        </IconCard>
      )}
    </div>
  );
}

function getIssuerListingSegment(listingSegment: IBrazilianCompanyIssuerListingSegment | null) {
  if (listingSegment === IBrazilianCompanyIssuerListingSegment.Tradicional) {
    return {
      label: "Tradicional",
      className: classNames("text-red-700"),
    };
  }

  if (
    listingSegment === IBrazilianCompanyIssuerListingSegment.Nivel_1 ||
    listingSegment === IBrazilianCompanyIssuerListingSegment.Nivel_2
  ) {
    return {
      label: listingSegment === IBrazilianCompanyIssuerListingSegment.Nivel_1 ? "Nível 1" : "Nível 2",
      className: classNames("text-amber-700"),
    };
  }

  if (listingSegment === IBrazilianCompanyIssuerListingSegment.NovoMercado) {
    return {
      label: "Novo Mercado",
      className: classNames("text-emerald-700"),
    };
  }

  if (
    listingSegment === IBrazilianCompanyIssuerListingSegment.BovespaMais ||
    listingSegment === IBrazilianCompanyIssuerListingSegment.BovespaMaisNivel_2
  ) {
    return {
      label:
        listingSegment === IBrazilianCompanyIssuerListingSegment.BovespaMais ? "Bovespa Mais" : "Bovespa Mais Nível 2",
      className: classNames("text-blue-700"),
    };
  }

  return null;
}

function getReclameAquiClassificationTextColor(finalScore: number): string {
  // Ótimo
  if (finalScore >= 8 && finalScore <= 10) {
    return "text-emerald-600 dark:text-emerald-400";
  }

  // Bom
  if (finalScore >= 7 && finalScore <= 7.9) {
    return "text-blue-700 dark:text-blue-400";
  }

  // Regular
  if (finalScore >= 6 && finalScore <= 6.9) {
    return "text-orange-600 dark:text-orange-400";
  }

  // Ruim
  if (finalScore >= 5 && finalScore <= 5.9) {
    return "text-red-500 dark:text-red-400";
  }

  // Não Recomendada
  return "text-purple-600 dark:text-purple-400";
}
